import React, { useState, useEffect, Fragment } from "react";
import { DateRange } from "react-date-range";
import { eachDayOfInterval, format } from "date-fns";
import { Button } from "react-bootstrap";
import {
  get_cookie,
  minimumDayErrorMessage,
  set_cookie,
} from "utils";
import { useDispatch, useSelector } from "react-redux";
import { getAvailableSlot } from "store/actions";
import moment from "moment";
import "moment-timezone";
import * as deviceObj from "react-device-detect";
import TimeDropdown from "../reservation-search-bar/TimeDropdown";
import { getBookableTimeOptions, pickBookableTime } from "utils/reservation";

const LA_TIMEZONE = "America/Los_Angeles";
const MIAMI_TIMEZONE = "America/New_York";
// Noon local time keeps a "YYYY-MM-DD" on the same calendar day in any timezone
const toLocalNoon = (date) => moment(date, "YYYY-MM-DD").hour(12).toDate();

const freezeCalendarScroll = (duration) => {
  const container = document.querySelector(".rdrInfiniteMonths");
  if (!container) return;

  const lockedTop = container.scrollTop;
  const lock = () => {
    if (container.scrollTop !== lockedTop) container.scrollTop = lockedTop;
  };

  container.addEventListener("scroll", lock);
  setTimeout(() => container.removeEventListener("scroll", lock), duration);
};

/*******************
  @Purpose : used for date range picker
  @Parameter : {dateObj,handleClickOpenCalendar,isFirstTimeLoad,setStartEndDateOnChange,startTimeOptionsHandler,tempDirections,tempScroll}
  @Author : INIC
  *******************/
function DateRangePicker({
  dateObj,
  handleClickOpenCalendar,
  isFirstTimeLoad,
  setStartEndDateOnChange,
  startTimeOptionsHandler,
  tempDirections,
  tempScroll,
  minimumDay,
  showMinimumErrorMessage,
  selectedStartDate,
  startDate: committedStartDate,
  setStartDate: setParentStartDate,
  endDate: committedEndDate,
  setEndDate: setParentEndDate,
  setDateObj,
  setEndDateMobile,
  vehicleId,
  setStartTimeDefaultValue,
  setStartDateMobile,
  setEndTimeDefaultValue, setIsVehicleEdit, isVehcileDetailsPage, minDayError, isAvailable,
  // Mobile-only sheet mode - combined header (title + close), Start/End
  // Date + time rows, and a visible Save/Reset footer. Passed by
  // MobileReservationSearchBar (home page hero) and SearchResultsToolbar's
  // own mobile layout; desktop ReservationSearchBar never passes these and
  // renders exactly as before.
  mobileSheet,
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  // Desktop anchored popover only - "above" (default) or "below" the field
  // that opened it. Passed by ReservationSearchBar (home page hero) based on
  // whether the search bar sits in the upper or lower half of the page.
  placement = "above",
}) {
  const { vehicleSlot } = useSelector(({ vehicle }) => vehicle);
  const { locations } = useSelector(({ common }) => common);
  const cityStateSlug = get_cookie("cityStateSlug") || "";
  const selectedLocation = locations?.find(
    (loc) =>
      `${loc.city_short_name?.toLowerCase()}_${loc.state_short_name?.toLowerCase()}` ===
      cityStateSlug.toLowerCase()
  );
  const isMiami = selectedLocation?.city_short_name?.toLowerCase() === "miami";
  const [state, setState] = useState(() =>
    committedStartDate && committedEndDate
      ? [{
        startDate: new Date(committedStartDate),
        endDate: new Date(committedEndDate),
        key: "selection",
      }]
      : dateObj
  );
  // Draft selection, local to this popover instance - only pushed up to the
  // parent (the reservation search bar's pill display) when Save is clicked.
  const [startDate, setStartDate] = useState(committedStartDate);
  const [endDate, setEndDate] = useState(committedEndDate);
  const [diffDays, setDiffDays] = useState(() =>
    committedStartDate && committedEndDate
      ? Math.max(1, Math.round(Math.abs((new Date(committedStartDate) - new Date(committedEndDate)) / 86400000)))
      : 0
  );
  const [clickCount, setClickCount] = useState(0);
  const [directions, setDirections] = useState(tempDirections);
  const [scroll, setScroll] = useState(tempScroll);
  const [isShowDataRangePicker, setIsShowDataRangePicker] = useState(false);
  const [minDayErrorMessage, setMinDayErrorMessage] = useState(minDayError || "");
  const [hoveredDate, setHoveredDate] = useState(null);
  const [hasHover, setHasHover] = useState(true);
  // Which of the mobile sheet's two time pickers is open ("start"/"end"/null) -
  // purely a local rendering concern, not needed outside this component.
  const [activeMobileTimeField, setActiveMobileTimeField] = useState(null);

  // Mobile sheet time pickers - only offer slots that are still bookable for
  // the draft dates (no past / inside-lead-time slots), and move the current
  // selection off a slot that just became unavailable.
  const mobileStartTimeOptions = getBookableTimeOptions(startDate);
  const mobileEndTimeOptions = getBookableTimeOptions(
    endDate,
    startDate && startDate === endDate ? parseInt(startTime, 10) : null
  );
  const mobileStartTimeOptionsKey = mobileStartTimeOptions.map((option) => `${option.id}${option.disabled ? "x" : ""}`).join(",");
  const mobileEndTimeOptionsKey = mobileEndTimeOptions.map((option) => `${option.id}${option.disabled ? "x" : ""}`).join(",");

  useEffect(() => {
    if (!mobileSheet || !onStartTimeChange) return;
    const next = pickBookableTime(mobileStartTimeOptions, startTime);
    if (next !== startTime) onStartTimeChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileSheet, mobileStartTimeOptionsKey, startTime]);

  useEffect(() => {
    if (!mobileSheet || !onEndTimeChange) return;
    const next = pickBookableTime(mobileEndTimeOptions, endTime);
    if (next !== endTime) onEndTimeChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileSheet, mobileEndTimeOptionsKey, endTime]);

  // State variables for disabled dates
  // vehicleSlot is a global Redux slice written by getAvailableSlot() for
  // whichever vehicle was last checked (e.g. on the vehicle-booking/details
  // flow) and is never reset on client-side navigation - only seed from it
  // when this picker instance actually belongs to a vehicle (vehicleId is
  // passed), otherwise a generic picker (home page hero, search-results
  // toolbar) inherits another vehicle's stale disabled dates.
  const [disabledDatesArr, setDisabledDatesArr] = useState(
    vehicleId
      ? vehicleSlot?.disabled_dates?.map((dateString) => {
        return toLocalNoon(dateString);
      }) || []
      : []
  );
  const [maxDate, setMaxDate] = useState(vehicleId ? vehicleSlot?.end_date || null : null);
  const [isDateInPastDate, setIsDateInPastDate] = useState([]);
  const [isDisabledDate, setIsDisabledDate] = useState([]);
  const [isDateBeforeStartDateArray, setIsDateBeforeStartDateArray] = useState([]);
  const [isDateAfterEndDateArray, setIsDateAfterEndDateArray] = useState([]);
  const [isAvailableEndDate, setIsAvailableEndDate] = useState(
    vehicleId
      ? vehicleSlot?.only_available_for_end_events?.map((dateString) => {
        return toLocalNoon(dateString);
      }) || []
      : []
  );
  const avablityRangeMonth = get_cookie("month_range")
  let isEndDateOnly = []
  const [isEndDateHovered, setIsEndDateHovered] = useState(false);
  // Computed disabled dates
  const [disabledDate, setDisabledDate] = useState([]);
  const getLocationInfo = () => {
    const cityName = get_cookie("selectedCityName") || "";
    const isMiami = cityName.toLowerCase().includes("miami");
    const tz = isMiami ? MIAMI_TIMEZONE : LA_TIMEZONE;
    return {
      city: isMiami ? "Miami" : "Los Angeles",
      time: moment().tz(tz).format("h:mm A"),
      zone: moment().tz(tz).format("z"),
    };
  };
  const [locationTime, setLocationTime] = useState(getLocationInfo);
  // for temporary unavilable vehicle date disabled
  const temporaryNotAvailable = isAvailable
    ? eachDayOfInterval({
      start: new Date(),
      end: new Date(new Date().setFullYear(new Date().getFullYear() + 2)),
    })
    : [];

  useEffect(() => {
    // Update disabledDate whenever any of its dependencies change
    const allDisabledDates = [
      ...isDateAfterEndDateArray,
      ...isDateBeforeStartDateArray,
      ...isDateInPastDate,
      ...isDisabledDate,
      ...isAvailableEndDate
    ].map((date) => {
      return toLocalNoon(date)
    });

    setDisabledDate(allDisabledDates);
  }, [isDateAfterEndDateArray, isDateBeforeStartDateArray, isDateInPastDate, isDisabledDate, isAvailableEndDate]);

  useEffect(() => {
    setLocationTime(getLocationInfo());
    const interval = setInterval(() => setLocationTime(getLocationInfo()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setHasHover(window.matchMedia("(hover: hover)").matches);
  }, []);

  const dispatch = useDispatch();

  /*******************
  @Purpose : used for count days
  @Parameter : {firstDate, secondDate}
  @Author : INIC
  *******************/
  const countDays = (firstDate, secondDate) => {
    const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
    let tempDays = Math.round(Math.abs((firstDate - secondDate) / oneDay));
    if (tempDays == 0) {
      tempDays = 1;
    }
    setDiffDays(tempDays);
    return tempDays;
  };

  /*******************
  @Purpose : React Hook used for set start and end dates
  @Parameter : {}
  @Author : INIC
  *******************/
  useEffect(() => {
    // checkAndSetStartDate();
    const isStacked = mobileSheet || window.innerWidth < 576;
    setDirections(isStacked ? "vertical" : "horizontal");
    setScroll({ enabled: isStacked });
    if (selectedStartDate && endDate) {
      setStartDate(format(state[0].startDate, "MMM d, yyyy", {}));
      setEndDate(format(state[0].endDate, "MMM d, yyyy", {}));
      countDays(state[0].startDate, state[0].endDate);
    }
    setIsShowDataRangePicker(true);
  }, []);

  // Update disabled dates when the calendar loads or changes
  useEffect(() => {
    updateDisabledDates(maxDate);
  }, [startDate, endDate, disabledDatesArr, maxDate]);

  // Function to calculate all disabled dates
  const updateDisabledDates = (maxDate, fullyUnavailableDates, date, onlyEndDateAvilable) => {

    // "Today" is the rental location's calendar day (LA / Miami), not the
    // browser's - otherwise a customer in a timezone ahead of the location
    // (e.g. Europe) sees the location's still-open today disabled as past.
    const locationToday = moment()
      .tz(isMiami ? MIAMI_TIMEZONE : LA_TIMEZONE)
      .format("YYYY-MM-DD");

    // Calculate past dates (last 30 days before the location's today)
    const pastDates = [];
    const cursor = moment(locationToday, "YYYY-MM-DD").subtract(30, "days");
    while (cursor.format("YYYY-MM-DD") < locationToday) {
      pastDates.push(cursor.format("YYYY-MM-DD"));
      cursor.add(1, "day");
    }
    // Miami doesn't allow same-day bookings — mark today as disabled
    if (isMiami) {
      pastDates.push(locationToday);
    }
    setIsDateInPastDate(pastDates);

    // Calculate dates before start date
    const beforeStartDates = [];
    if (startDate && date && !endDate) {
      const startDay = moment(startDate, "MMM D, YYYY").format("YYYY-MM-DD");
      const tempDate = moment(locationToday, "YYYY-MM-DD");

      while (tempDate.format("YYYY-MM-DD") < startDay) {
        // Only add if it's not already in past dates
        const formattedDate = tempDate.format("YYYY-MM-DD");
        if (!pastDates.includes(formattedDate)) {
          beforeStartDates.push(formattedDate);
        }
        tempDate.add(1, "day");
      }
      setIsDateBeforeStartDateArray(beforeStartDates);
    } else {
      setIsDateBeforeStartDateArray([]);
    }


    // Calculate dates after end date/max date
    const afterEndDates = [];
    const maxDateToUse = maxDate
      ? new Date(`${maxDate}T00:00:00Z`) // Force UTC midnight
      : endDate
        ? new Date(moment(endDate, "MMM D, YYYY").format("YYYY-MM-DD") + "T00:00:00Z")
        : null;


    if (maxDateToUse) {
      const tempDate = new Date(maxDateToUse.getTime());
      tempDate.setUTCDate(tempDate.getUTCDate() + 1); // Fix: Use setUTCDate() instead of setDate()

      const futureLimit = new Date();
      futureLimit.setUTCFullYear(futureLimit.getUTCFullYear() + 2); // Use UTCFullYear for consistency

      while (tempDate <= futureLimit) {
        afterEndDates.push(moment.utc(tempDate).format("YYYY-MM-DD")); // Ensure UTC formatting
        tempDate.setUTCDate(tempDate.getUTCDate() + 1);
      }
    }
    if (startDate && !endDate) {
      setIsDateAfterEndDateArray(afterEndDates);
    }
    const isDisabled = fullyUnavailableDates || disabledDatesArr
    // Get disabled dates from API
    const formattedDisabledDates = Array.isArray(isDisabled)
      ? isDisabled.map(d => moment(d).format("YYYY-MM-DD"))
      : [];
    setIsDisabledDate(formattedDisabledDates);

    const isEndDate = onlyEndDateAvilable || isAvailableEndDate
    if (isEndDate?.length > 0) {
      const formattedEndDates = Array.isArray(isEndDate)
        ? isEndDate.map(d => moment(d).format("YYYY-MM-DD"))
        : [];
      setIsAvailableEndDate(formattedEndDates);

    }
  };

  const handleClearDate = () => {
    set_cookie("daterange", "");
    set_cookie("timerange", "");
    setIsVehicleEdit && setIsVehicleEdit(false)
    setMinDayErrorMessage("")
    // Reset the state completely
    setState([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: "selection",
      },
    ]);

    // Reset all other state variables
    setMaxDate(null);
    setStartTimeDefaultValue(null);
    setEndTimeDefaultValue(null);
    setStartEndDateOnChange({});
    setStartDate(null);
    setEndDate(null);
    setDiffDays(0);
    setClickCount(0);

    // Clear the disabled date arrays
    setIsDateAfterEndDateArray([]);
    setIsDateBeforeStartDateArray([]);
    setIsDisabledDate([]);
    // Clear the cookie to ensure the today circle is handled correctly
    set_cookie("removeCircleClassFlag", "");

    // Reset the month view
    handleMonthChange(null, "start_date");
  };

  /*******************
  @Purpose : Setter for start date based to current time
  @Parameter : {}
  @Author : INIC
  *******************/
  const vehicleAvailableForDriveNow = useSelector(
    (state) => state?.vehicle?.vehicleAvailableForDriveNow
  );

  const checkAndSetStartDate = () => {
    const timezone = isMiami ? MIAMI_TIMEZONE : LA_TIMEZONE;

    const nowInTz = moment().tz(timezone);

    // The calendar renders browser-local days, so hand it the location's
    // calendar day as a local date (not the instant of the location's
    // midnight, which can fall on a different local day).
    const toLocalDay = (m) => moment(m.format("YYYY-MM-DD"), "YYYY-MM-DD").toDate();

    // Miami never allows same-day bookings — always start from tomorrow
    if (isMiami) {
      return toLocalDay(nowInTz.clone().add(1, "day"));
    }

    const dayOfWeek = nowInTz.day(); // 0 = Sunday, 6 = Saturday
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    const closingHour = vehicleAvailableForDriveNow
      ? (isWeekend ? 17 : 19) // 5PM weekend / 7PM weekday (drive-now)
      : (isWeekend ? 16 : 18); // 4PM weekend / 6PM weekday

    return nowInTz.hour() >= closingHour
      ? toLocalDay(nowInTz.clone().add(1, "day"))
      : toLocalDay(nowInTz);
  };

  /*******************
  @Purpose : set remove circle class today date
  @Parameter : {}
  @Author : INIC
  *******************/
  useEffect(() => {
    if (startDate && endDate) {
      removeClassRemoveCircle();
      set_cookie("removeCircleClassFlag", 1, 1);
    } else {
      set_cookie("removeCircleClassFlag", "");

      // Add a small delay to ensure DOM is ready
      setTimeout(() => {
        // addClassRemoveCircle();

        // Add event listener only if element exists
        const todayElement = document.querySelector(".rdrDayToday");
        if (todayElement) {
          todayElement.addEventListener("mouseleave", function (e) {
            if (get_cookie("removeCircleClassFlag")) {
              removeClassRemoveCircle();
            } else {
              // addClassRemoveCircle();P
            }
          });
        }
      }, 100);
    }

    // Clean up event listener when component unmounts
    return () => {
      const todayElement = document.querySelector(".rdrDayToday");
      if (todayElement) {
        todayElement.removeEventListener("mouseleave", function (e) {
          // Same function as above
        });
      }
    };
  }, [isShowDataRangePicker, startDate]);

  const addClassRemoveCircle = () => {
    const todayElement = document.querySelector(".rdrDayToday");
    if (todayElement) {
      todayElement.classList.add("removeCircle");
    }
  };

  const removeClassRemoveCircle = () => {
    const todayElement = document.querySelector(".rdrDayToday");
    if (todayElement) {
      todayElement.classList.remove("removeCircle");
    }
  };

  const handleMonthChange = (date, flag) => {
    // Get the start of the first visible month
    const startMonth = date ? moment(date).startOf("month") : moment().startOf("month");
    const start_date = date && startDate && !endDate ? moment(startDate).startOf("month").format("YYYY-MM-DD") : startMonth.format("YYYY-MM-DD");
    // Get the end of the second visible month
    const endMonth = moment(startMonth).add(1, "month").endOf("month");
    const end_date = endMonth.format("YYYY-MM-DD");
    const currentLocationId = get_cookie("currentLocationId");
    const selectedDate = moment(selectedStartDate).format("YYYY-MM-DD");
    if (currentLocationId && vehicleId) {
      dispatch(getAvailableSlot(currentLocationId, vehicleId, start_date, end_date, flag, selectedDate)).then((res) => {
        if (res) {
          res?.event !== "start_date" && setIsAvailableEndDate([])
          setIsDateAfterEndDateArray([]);
          setIsDateBeforeStartDateArray([]);
          setIsDisabledDate([]);
          setMaxDate(res.end_date)
          set_cookie("month_range", { startDate: start_date, endDate: end_date })
          const fullyUnavailableDates =
            res?.disabled_dates?.map((dateString) => {
              return toLocalNoon(dateString);
            }) || [];
          const onlyEndDateAvilable = res?.only_available_for_end_events?.map((dateString) => {
            return toLocalNoon(dateString);
          }) || []
          setDisabledDatesArr(fullyUnavailableDates);
          setIsAvailableEndDate(res?.event !== "start_date" ? [] : onlyEndDateAvilable)
          // Trigger a recalculation of disabled dates
          updateDisabledDates(res.end_date, fullyUnavailableDates, date, res?.event !== "start_date" ? [] : onlyEndDateAvilable);
        }
      });
    }
  }

  //call api while calendar open
  useEffect(() => {
    if (startDate && isShowDataRangePicker && temporaryNotAvailable?.length === 0) {
      handleMonthChange(startDate, startDate && !endDate || startDate && endDate && !isShowDataRangePicker ? "end_date" : "start_date");
    } else if (startDate && isShowDataRangePicker && minDayErrorMessage && temporaryNotAvailable?.length === 0) {
      handleMonthChange(startDate, "start_date");
    } else if (!startDate && isShowDataRangePicker && !avablityRangeMonth && temporaryNotAvailable?.length === 0) {
      handleMonthChange(null, "start_date");
    }
  }, [isShowDataRangePicker, startDate, minDayErrorMessage])

  // Function to check if a date is in the past
  const isPastDate = (date) => {
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000) < today;
  };

  // Helper function to safely convert and compare dates
  const isSameDay = (date1, date2) => {
    const d1 = date1 instanceof Date ? date1 : new Date(date1);
    const d2 = date2 instanceof Date ? date2 : new Date(date2);

    if (isNaN(d1) || isNaN(d2)) return false;

    const utcD1 = new Date(d1.getTime() - d1.getTimezoneOffset() * 60000);
    const utcD2 = new Date(d2.getTime() - d2.getTimezoneOffset() * 60000);
    return (
      utcD1.toISOString().split("T")[0] === utcD2.toISOString().split("T")[0]
    );
  };

  const dayContentRenderer = (date) => {
    const formattedDate = moment(date).format("YYYY-MM-DD");
    // Highlight the rental location's today (LA / Miami), not the browser's.
    const locationToday = moment()
      .tz(isMiami ? MIAMI_TIMEZONE : LA_TIMEZONE)
      .format("YYYY-MM-DD");

    const isDateInPast = isDateInPastDate.includes(formattedDate);
    const isDateBeforeStartDate = isDateBeforeStartDateArray.includes(formattedDate);
    const isDateAfterEndDate = isDateAfterEndDateArray.includes(formattedDate);
    const isDisabled = isDisabledDate.includes(formattedDate);
    const isEndDateOnly = isAvailableEndDate.includes(formattedDate);
    const isHovered = hoveredDate === formattedDate;
    // The library renders a solid hover-preview circle behind whichever day
    // is hovered (see .rdrDayStartPreview/.rdrDayInPreview/.rdrDayEndPreview
    // in _datepicker.scss) - text needs to go white there too, or it's
    // invisible against that dark fill, same as on a confirmed selection.
    const isUnavailableForSelection =
      temporaryNotAvailable?.length !== 0 ||
      isDateInPast ||
      isDisabled ||
      isDateBeforeStartDate ||
      isDateAfterEndDate;
    const isConfirmedSelection =
      (startDate && isSameDay(date, startDate) && temporaryNotAvailable?.length === 0) ||
      (endDate && isSameDay(date, endDate) && temporaryNotAvailable?.length === 0);

    return (
      <div
        className={`calendar-day-content ${(startDate && isSameDay(date, startDate)) ? "start-date-blue" : ""}`}
        style={{
          border: "none",
          padding: "0",
          boxSizing: "border-box",
          textDecoration:
            isConfirmedSelection
              ? "none"
              : (temporaryNotAvailable?.length !== 0 || isDateInPast || isDisabled || isDateBeforeStartDate || (isDateAfterEndDate && !isEndDateOnly))
                ? "line-through"
                : "none",
          color:
            isConfirmedSelection || (isHovered && !isUnavailableForSelection)
              ? "#fff"
              : isUnavailableForSelection
                ? "#a0a0a0"
                : isEndDateOnly ? "#a0a0a0" : formattedDate === locationToday ? undefined : "black",
          cursor: isDateInPast || isDisabled || isDateBeforeStartDate || isDateAfterEndDate || isEndDateOnly ? "default" : "pointer",
        }}
        onMouseEnter={hasHover ? () => {
          setHoveredDate(formattedDate);
          if (isEndDateOnly) setIsEndDateHovered(true);
        } : undefined}
        onMouseLeave={hasHover ? () => {
          setHoveredDate(null);
          setIsEndDateHovered(false);
        } : undefined}
        onClick={() => {
          if ((deviceObj.isMobile || deviceObj.isIOS) && isEndDateOnly) {

            setHoveredDate(formattedDate);
            setIsEndDateHovered(true);

          }
        }}
      >
        {date.getDate()}
        {isEndDateOnly && isHovered && (
          <div className={`end-date-hovered ${isEndDateOnly && date.getDay() === 6 ? "end-date-saturday" : ""}
    ${isEndDateOnly && date.getDay() === 0 ? "end-date-sunday" : ""}`}>Return date only</div>
        )}
      </div>
    );
  };


  useEffect(() => {
    if (deviceObj.isMobile || deviceObj.isIOS && isEndDateHovered) {
      setTimeout(() => {
        setHoveredDate(null)
        setIsEndDateHovered(false);
      }, 1000);
    }
  }, [isEndDateHovered])
  /*******************
  @purpose : Render HTML/ React Components
  @Author : INIC
  ******************/
  return (
    <Fragment>
      {isShowDataRangePicker && (
        <div className={`d-flex align-items-end align-items-lg-center justify-content-center box-calendar ${mobileSheet ? "mobile-calendar-overlay" : ""} ${placement === "below" ? "box-calendar--below" : ""} ${isVehcileDetailsPage ? "box-calendar--vehicle-details" : ""}`}>
          <div className={`d-flex flex-column position-relative date-range-box date-range-box-larger ${!deviceObj.isMobile && !deviceObj.isIOS && isEndDateHovered ?
            "overflow-hovered" : ""
            } ${mobileSheet ? "mobile-calendar-sheet" : ""}`} style={{padding: mobileSheet ? 0 : 20}}>
            {!mobileSheet && (
              <>
                <Button
                  variant="link"
                  className="close-btn d-none d-lg-inline-block"
                  onClick={() => {
                    // setMinDayErrorMessage("");
                    handleClickOpenCalendar()
                    temporaryNotAvailable?.length === 0 && handleMonthChange(startDate, startDate && endDate || startDate && !endDate ? "end_date" : "start_date");
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 6L18 18" stroke="#202124" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M18 6L6.00003 18" stroke="#202124" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Button>

                {/* Mobile top section */}
                <div className="d-flex d-lg-none justify-content-between align-items-center mt-0 date-footer">
                  <span className="text-danger font-14 ff-hel-med lh-normal">
                    {minDayErrorMessage}
                  </span>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      // setMinDayErrorMessage("");
                      handleClickOpenCalendar()
                      temporaryNotAvailable?.length === 0 && handleMonthChange(startDate, startDate && endDate || startDate && !endDate ? "end_date" : "start_date");
                    }}
                  >
                    <path d="M6 6L18 18" stroke="#202124" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M18 6L6.00003 18" stroke="#202124" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>

                <div className="selected-date">
                  <span className='selected-date-header'>
                    {startDate && endDate
                      ? diffDays > 1
                        ? diffDays + " days"
                        : diffDays + " day"
                      : clickCount > 0
                        ? "Select End Date"
                        : "Select Start Date"}
                  </span>
                  {minDayErrorMessage && (
                    <span className="d-none d-tab-block text-danger font-14">
                      {minDayErrorMessage}
                    </span>
                  )}
                </div>
              </>
            )}

            {mobileSheet && (
              <div className="mobile-calendar-header">
                <span className="mobile-calendar-title">
                  {startDate && endDate
                    ? diffDays > 1
                      ? diffDays + " days"
                      : diffDays + " day"
                    : clickCount > 0
                      ? "Select end date"
                      : "Select start date"}
                </span>
                <button
                  type="button"
                  className="mobile-calendar-close"
                  onClick={() => {
                    handleClickOpenCalendar();
                    temporaryNotAvailable?.length === 0 && handleMonthChange(startDate, startDate && endDate || startDate && !endDate ? "end_date" : "start_date");
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 6L18 18" stroke="#202124" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M18 6L6.00003 18" stroke="#202124" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            )}

            <div className={mobileSheet ? "mobile-calendar-body" : ""} style={{marginRight: 5}}>
            <DateRange
              className="date-range"
              dragSelectionEnabled={false}
              showPreview={hasHover}
              minDate={checkAndSetStartDate()}
              // Mobile sheet scrolls through every month up to maxDate (see
              // .rdrInfiniteMonths) - 2 years' worth is far more scrolling
              // than needed there, so cap it to 6 months out instead.
              // Desktop's popover keeps the full 2-year range.
              maxDate={
                mobileSheet
                  ? new Date(new Date().setMonth(new Date().getMonth() + 6))
                  : new Date(new Date().setFullYear(new Date().getFullYear() + 2))
              } // Set to 2 years in the future (6 months on the mobile sheet)
              startDatePlaceholder="Start day"
              endDatePlaceholder="End day"
              weekdayDisplayFormat="EEEEEE"
              monthDisplayFormat="MMMM yyyy"
              weekStartsOn={mobileSheet ? 1 : undefined}
              onShownDateChange={(e) => {
                if (e && temporaryNotAvailable?.length === 0) {
                  handleMonthChange(e, startDate && !endDate ? "end_date" : "start_date")
                  set_cookie("removeCircleClassFlag", "");
                  // Add a small delay to ensure DOM has updated
                  // setTimeout(() => {
                  //   addClassRemoveCircle();
                  // }, 500);
                }
              }
              }
              onChange={(value) => {
                let errorMsg = "";
                let selectedStartDate = "";
                let selectedEndDate = "";
                // react-date-range's own calcNewSelection already reverses
                // startDate/endDate on the second click when the newly
                // clicked date is before the first-clicked anchor, so
                // value.selection is always chronological here - just trust
                // it rather than re-anchoring to the (possibly stale) old
                // startDate.
                setState([value.selection]);
                setMinDayErrorMessage(errorMsg);
                if (showMinimumErrorMessage) {
                  showMinimumErrorMessage(errorMsg);
                }
                if (clickCount == 0 && !startDate) {
                  setClickCount(1);
                  setStartDate(
                    format(value.selection.startDate, "MMM d, yyyy", {})
                  );
                  setEndDate(null);
                  setDiffDays(0);
                  if (isVehcileDetailsPage) {
                    setParentStartDate &&
                      setParentStartDate(
                        format(value.selection.startDate, "MMM d, yyyy", {})
                      );
                    setParentEndDate && setParentEndDate(null);
                  }
                  freezeCalendarScroll(100);
                } else if (clickCount == 0 && endDate) {
                  freezeCalendarScroll(700);
                  setDateObj([
                    {
                      startDate: new Date(),
                      endDate: new Date(),
                      key: "selection",
                    },
                  ])
                  setClickCount(1);
                  setStartDate(
                    format(value.selection.startDate, "MMM d, yyyy", {})
                  );
                  setStartDateMobile && setStartDateMobile(format(value.selection.startDate, "MMM d", {}))
                  setEndDate(null);
                  setEndDateMobile && setEndDateMobile(null)
                  setDiffDays(0);
                  if (isVehcileDetailsPage) {
                    setParentStartDate &&
                      setParentStartDate(
                        format(value.selection.startDate, "MMM d, yyyy", {})
                      );
                    setParentEndDate && setParentEndDate(null);
                  }
                } else {
                  setClickCount(0);
                  let selectedDays = countDays(
                    value.selection.startDate,
                    value.selection.endDate
                  );
                  selectedStartDate = format(value.selection.startDate, "MMM d, yyyy", {});
                  selectedEndDate = format(value.selection.endDate, "MMM d, yyyy", {});
                  setStartDate(selectedStartDate);
                  setEndDate(selectedEndDate);
                  startTimeOptionsHandler(value.selection);

                  if (isVehcileDetailsPage) {
                    setStartEndDateOnChange(value.selection);
                  }

                  if (selectedDays < minimumDay) {
                    errorMsg = minimumDayErrorMessage(minimumDay);
                    setMinDayErrorMessage(errorMsg);
                    if (showMinimumErrorMessage) {
                      showMinimumErrorMessage(errorMsg);
                    }
                  }
                }
              }}
              moveRangeOnFirstSelection={false}
              showSelectionPreview={false}
              preventSnapRefocus={true}
              ranges={state}
              months={2}
              showMonthAndYearPickers={false}
              showDateDisplay={false}
              editableDateInputs={false}
              direction={directions}
              scroll={scroll}
              disabledDates={temporaryNotAvailable?.length > 0 ? temporaryNotAvailable : disabledDate}
              dayContentRenderer={dayContentRenderer}
            />
            </div>

            {!mobileSheet && (
              <div className="d-none d-lg-flex justify-content-between align-items-center date-footer">
                <span
                  className="calendar-reset-button"
                  onClick={() => {handleClearDate()}}
                >
                  Reset
                </span>

                <Button
                  variant="primary btn-save-date radius-8"
                  onClick={() => {
                    setStartEndDateOnChange({
                      startDate: new Date(startDate),
                      endDate: new Date(endDate),
                      key: "selection",
                    });
                    handleClickOpenCalendar();
                    temporaryNotAvailable?.length === 0 && handleMonthChange(startDate, startDate && endDate || startDate && !endDate ? "end_date" : "start_date");
                  }}
                  disabled={!startDate || !endDate || !!minDayErrorMessage}
                >
                  Save
                </Button>
              </div>
            )}

            {mobileSheet && (
              <div className="mobile-calendar-footer">
                <div className="mobile-calendar-datetime-row">
                  <div
                    className={`mobile-calendar-date-pill ${
                      !startDate ? "is-active" : ""
                    }`}
                  >
                    <span
                      className={`mobile-calendar-date-pill-label ${
                        startDate ? "" : "is-placeholder"
                      }`}
                    >
                      {startDate || "Start Date"}
                    </span>
                  </div>
                  <TimeDropdown
                    mobile
                    value={startTime}
                    onChange={onStartTimeChange}
                    options={mobileStartTimeOptions}
                    isOpen={activeMobileTimeField === "start"}
                    onToggle={() =>
                      setActiveMobileTimeField((prev) =>
                        prev === "start" ? null : "start"
                      )
                    }
                    onClose={() => setActiveMobileTimeField(null)}
                  />
                </div>

                <div className="mobile-calendar-datetime-row">
                  <div
                    className={`mobile-calendar-date-pill ${
                      startDate && !endDate ? "is-active" : ""
                    }`}
                  >
                    <span
                      className={`mobile-calendar-date-pill-label ${
                        endDate ? "" : "is-placeholder"
                      }`}
                    >
                      {endDate || "End Date"}
                    </span>
                  </div>
                  <TimeDropdown
                    mobile
                    value={endTime}
                    onChange={onEndTimeChange}
                    options={mobileEndTimeOptions}
                    isOpen={activeMobileTimeField === "end"}
                    onToggle={() =>
                      setActiveMobileTimeField((prev) =>
                        prev === "end" ? null : "end"
                      )
                    }
                    onClose={() => setActiveMobileTimeField(null)}
                  />
                </div>

                <div className="mobile-calendar-actions">
                  <span
                    className={`calendar-reset-button ${startDate ? "is-enabled" : ""}`}
                    onClick={() => {
                      handleClearDate();
                      setActiveMobileTimeField(null);
                      onStartTimeChange && onStartTimeChange("10:00");
                      onEndTimeChange && onEndTimeChange("10:00");
                    }}
                  >
                    Reset
                  </span>

                  <Button
                    variant="primary btn-save-date radius-8"
                    onClick={() => {
                      setStartEndDateOnChange({
                        startDate: new Date(startDate),
                        endDate: new Date(endDate),
                        key: "selection",
                      });
                      handleClickOpenCalendar();
                      temporaryNotAvailable?.length === 0 && handleMonthChange(startDate, startDate && endDate || startDate && !endDate ? "end_date" : "start_date");
                    }}
                    disabled={!startDate || !endDate || !!minDayErrorMessage}
                  >
                    Save
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Fragment>
  );
}
export default DateRangePicker;
